import { useState, type ReactNode } from "react"
import * as Validator from "yup"
import {
	Badge,
	Box,
	Button,
	Divider,
	Icons,
	Inline,
	Stack,
	TabContent,
	TabItem,
	Tabs,
	TabsList,
	Text,
	noSpinnersClassName,
} from "@sembark-travel/ui-base"
import { Dialog, DialogBody } from "@sembark-travel/ui-dialog"
import {
	CheckboxInputField,
	ImageCropInputField,
	ErrorMessage,
	Form,
	GetFieldValue,
	RadioInputField,
	SubmissionError,
	TextInputField,
	useFieldValue,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { acceptedImageTypes, imageFileValidator } from "../shared/utils"
import { AmenitiesField, type TAmenity } from "../Amenities"
import { useHotelOtaMealPlans } from "../HotelOtaMealPlans"
import {
	HotelConfigLockButton,
	type TUnlockHotelConfigCopy,
} from "../shared/HotelConfigLock"
import { emptyOtaRoomTypeFormValues } from "./utils"

export interface IOtaRoomTypeFormSchema {
	name: string
	no_of_rooms: string | undefined
	base_adult_occupancy: string | undefined
	max_occupancy: string | undefined
	max_adult_occupancy: string | undefined
	max_child_occupancy: string | undefined
	image?: File
	applicable_meal_plans: "all" | "specific"
	ota_meal_plans: Array<number>
	amenities: Array<TAmenity>
}

// Every field except `amenities` lives on the "Room details" tab — used to
// check for and surface errors on that tab before letting "Next" proceed.
const roomDetailsFieldNames: Array<keyof IOtaRoomTypeFormSchema> = [
	"name",
	"no_of_rooms",
	"base_adult_occupancy",
	"max_occupancy",
	"max_adult_occupancy",
	"max_child_occupancy",
	"image",
	"applicable_meal_plans",
	"ota_meal_plans",
]

// What an edit has to unlock first: the occupancy and the meal plans the room
// is sold with.
const lockedFieldNames: Array<keyof IOtaRoomTypeFormSchema> = [
	"base_adult_occupancy",
	"max_occupancy",
	"max_adult_occupancy",
	"max_child_occupancy",
	"applicable_meal_plans",
	"ota_meal_plans",
]

function otaRoomTypeValidationSchema({
	requireImage,
	withMealPlanPicker,
}: {
	requireImage: boolean
	withMealPlanPicker: boolean
}) {
	return Validator.object().shape({
		name: Validator.string()
			.trim()
			.min(2, "Should be at least 2 characters")
			.max(51, "Should be less than 51 characters")
			.required("Name is required"),
		no_of_rooms: Validator.number()
			.min(1, "Should be at least 1")
			.max(500, "Should be ≤ 500")
			.required("Required"),
		base_adult_occupancy: Validator.number()
			.required("Required")
			.min(1, "Should be ≥ 1")
			.test("base-adults-lte-max-adults", "", function (value) {
				const { max_adult_occupancy } = this.parent
				if (!Number.isFinite(value) || !Number.isFinite(max_adult_occupancy))
					return true
				return value <= max_adult_occupancy
					? true
					: this.createError({
							message: `Must be ≤ Max Adults (${max_adult_occupancy})`,
						})
			}),
		max_occupancy: Validator.number()
			.required("Required")
			.min(1, "Should be ≥ 1")
			.max(50, "Should be ≤ 50")
			.test("max-occupancy-gte-base-adults", "", function (value) {
				const { base_adult_occupancy } = this.parent
				if (!Number.isFinite(value) || !Number.isFinite(base_adult_occupancy))
					return true
				return value >= base_adult_occupancy
					? true
					: this.createError({
							message: `Must be ≥ Base Adults (${base_adult_occupancy})`,
						})
			}),
		max_adult_occupancy: Validator.number()
			.required("Required")
			.min(1, "Should be ≥ 1")
			.test("max-adults-lte-max-occupancy", "", function (value) {
				const { max_occupancy } = this.parent
				if (!Number.isFinite(value) || !Number.isFinite(max_occupancy))
					return true
				return value <= max_occupancy
					? true
					: this.createError({
							message: `Must be ≤ Max Occupancy (${max_occupancy})`,
						})
			}),
		max_child_occupancy: Validator.number()
			.required("Required")
			.min(0, "Must be ≥ 0")
			.test("max-children-lte-max-occupancy", "", function (value) {
				const { max_occupancy } = this.parent
				if (!Number.isFinite(value) || !Number.isFinite(max_occupancy))
					return true
				return value <= max_occupancy
					? true
					: this.createError({
							message: `Must be ≤ Max Occupancy (${max_occupancy})`,
						})
			}),
		image: imageFileValidator(
			requireImage ? "Please select a room image" : undefined
		),
		applicable_meal_plans: Validator.mixed().oneOf(["all", "specific"]),
		ota_meal_plans: Validator.array()
			.of(Validator.number())
			.when("applicable_meal_plans", ([value], schema) =>
				withMealPlanPicker && value === "specific"
					? schema.min(1, "Please select at least one meal plan")
					: schema
			),
		amenities: Validator.array()
			.required("Please select at least one amenity")
			.min(1, "Please select at least one amenity"),
	})
}

function OtaMealPlansField({
	hotelId,
	name,
	disabled,
}: {
	hotelId: number
	name: string
	disabled: boolean
}) {
	const { mealPlans, isFetching } = useHotelOtaMealPlans(hotelId)
	const { value } = useFieldValue<Array<number>>(name)
	const selected = value || []
	return (
		<Stack gap="2">
			<Text fontWeight="semibold">
				Select the meal plan that apply to this room
			</Text>
			{isFetching ? (
				<Inline gap="2" alignItems="center">
					<Icons.Spinner />
					<Text color="muted">Loading meal plans...</Text>
				</Inline>
			) : !mealPlans?.length ? (
				<Text color="muted">No meal plans added for this hotel yet.</Text>
			) : (
				<Inline gap="2" flexWrap="wrap">
					{mealPlans.map((mealPlan) => {
						const isSelected = selected.includes(mealPlan.id)
						return (
							<Box
								key={mealPlan.id}
								paddingX="3"
								paddingY="2"
								borderWidth="1"
								rounded="md"
								borderColor={isSelected ? "primary_emphasis" : "default"}
								bgColor={isSelected ? "primary" : "default"}
							>
								<CheckboxInputField
									name={name}
									value={mealPlan.id}
									label={
										<Inline gap="2" alignItems="center">
											{mealPlan.name}
											<Badge>{mealPlan.meal_category}</Badge>
										</Inline>
									}
									disabled={disabled}
									hideErrors
								/>
							</Box>
						)
					})}
				</Inline>
			)}
			<ErrorMessage name={name} />
		</Stack>
	)
}

export function OtaRoomTypeForm({
	submitLabel,
	initialValues = emptyOtaRoomTypeFormValues,
	initialImageUrl,
	hotelId,
	withMealPlanPicker = false,
	notice,
	unlockCopy,
	onSubmit,
}: {
	submitLabel: string
	// only passed when editing
	initialValues?: IOtaRoomTypeFormSchema
	initialImageUrl?: string
	hotelId?: number
	withMealPlanPicker?: boolean
	// shown once the form is unlocked, or always when there is nothing to unlock
	notice?: ReactNode
	// only passed when editing, the form then starts with the occupancy and meal plans locked
	unlockCopy?: TUnlockHotelConfigCopy
	onSubmit: (values: IOtaRoomTypeFormSchema) => Promise<void> | void
}) {
	const [selectedTab, setSelectedTab] = useState<"details" | "amenities">(
		"details"
	)
	const [isLocked, setIsLocked] = useState(Boolean(unlockCopy))
	return (
		<Form<IOtaRoomTypeFormSchema>
			initialValues={initialValues}
			validate={validateFormValues(
				otaRoomTypeValidationSchema({
					requireImage: !initialImageUrl,
					withMealPlanPicker,
				})
			)}
			onSubmit={withServerErrors(async (values) => {
				await onSubmit(
					withMealPlanPicker
						? values
						: {
								...values,
								applicable_meal_plans: "all",
								ota_meal_plans: [],
							}
				)
			})}
			subscription={{ submitting: true, errors: true, submitFailed: true }}
		>
			{({ handleSubmit, submitting, errors, submitFailed, form }) => {
				// details must be valid before moving on to the amenities tab
				function goToAmenities() {
					const { errors: currentErrors } = form.getState()
					const hasDetailsErrors = roomDetailsFieldNames.some(
						(name) => currentErrors?.[name]
					)
					if (hasDetailsErrors) {
						roomDetailsFieldNames.forEach((name) => form.blur(name))
						return
					}
					setSelectedTab("amenities")
				}
				return (
					<form noValidate onSubmit={handleSubmit}>
						<Stack gap="4">
							<Tabs layout="col">
								<TabsList>
									<TabItem
										active={selectedTab === "details"}
										onClick={() => setSelectedTab("details")}
									>
										Room details
									</TabItem>
									<TabItem
										active={selectedTab === "amenities"}
										onClick={goToAmenities}
									>
										Amenities
									</TabItem>
								</TabsList>
								<TabContent active={selectedTab === "details"} padding="6">
									<Stack gap="4">
										{isLocked ? null : notice}
										<Inline gap="4" flexWrap="wrap">
											<Stack gap="4" style={{ maxWidth: "500px" }}>
												<Inline gap="4" flexWrap="wrap">
													<TextInputField
														name="name"
														label="Name"
														type="text"
														placeholder="e.g. Deluxe Room"
														style={{ minWidth: "20rem" }}
													/>
													<TextInputField
														name="no_of_rooms"
														label="No of Rooms"
														type="number"
														placeholder="e.g. 20"
														min={1}
														style={{ maxWidth: "8rem" }}
														className={noSpinnersClassName}
													/>
													<Inline gap="4">
														<GuestCountInputField
															name="base_adult_occupancy"
															label="Base Adults"
															min={0}
															disabled={isLocked}
														/>
													</Inline>
													<Inline gap="4" flexWrap="wrap">
														<GuestCountInputField
															name="max_occupancy"
															label="Max Occupancy"
															min={1}
															disabled={isLocked}
														/>
														<GuestCountInputField
															name="max_adult_occupancy"
															label="Max Adults"
															min={0}
															disabled={isLocked}
														/>
														<GuestCountInputField
															name="max_child_occupancy"
															label="Max Children"
															min={0}
															disabled={isLocked}
														/>
													</Inline>
												</Inline>
												{withMealPlanPicker && hotelId ? (
													<ApplicableMealPlansField
														hotelId={hotelId}
														disabled={isLocked}
													/>
												) : null}
											</Stack>
											<ImageCropInputField
												preview
												crop={{ aspect: 3 / 2, minHeight: 100 }}
												name="image"
												label="Room Image"
												initialImageUrl={initialImageUrl}
												altText={initialValues.name}
												accept={acceptedImageTypes.join(",")}
											/>
										</Inline>
									</Stack>
								</TabContent>
								<TabContent active={selectedTab === "amenities"} padding="6">
									<Stack gap="4">
										{isLocked ? null : notice}
										<AmenitiesField name="amenities" type="room" />
									</Stack>
								</TabContent>
							</Tabs>
							<Box paddingX="6">
								<SubmissionError />
								{submitFailed &&
								roomDetailsFieldNames.some((name) => errors?.[name]) ? (
									<Box as="span" fontSize="sm" color="danger">
										Error in room details
									</Box>
								) : null}
							</Box>
							<Divider marginY="0" />
							<Inline
								gap="4"
								padding="4"
								paddingTop="0"
								alignItems="center"
								justifyContent={unlockCopy ? "between" : "end"}
							>
								{unlockCopy ? (
									<HotelConfigLockButton
										{...unlockCopy}
										label="room"
										isLocked={isLocked}
										onUnlock={() => setIsLocked(false)}
										onLock={() => {
											form.batch(() => {
												lockedFieldNames.forEach((name) =>
													form.change(name, initialValues[name])
												)
											})
											setIsLocked(true)
										}}
									/>
								) : null}
								{selectedTab === "details" ? (
									<Button
										type="button"
										level="primary"
										disabled={submitting}
										onClick={(e) => {
											e.preventDefault()
											goToAmenities()
										}}
									>
										Next
									</Button>
								) : (
									<Button type="submit" disabled={submitting}>
										{submitting ? <Icons.Spinner /> : null}{" "}
										{submitting ? "Saving..." : submitLabel}
									</Button>
								)}
							</Inline>
						</Stack>
					</form>
				)
			}}
		</Form>
	)
}

function ApplicableMealPlansField({
	hotelId,
	disabled,
}: {
	hotelId: number
	disabled: boolean
}) {
	return (
		<Stack gap="2">
			<Box fontWeight="semibold">Applicable Meal plans</Box>
			<GetFieldValue<"all" | "specific"> name="applicable_meal_plans">
				{({ value, onChange }) => (
					<Inline gap="4" maxWidth="lg" flexWrap="wrap">
						<Box
							flex="1"
							paddingX="4"
							paddingY="2"
							borderWidth="1"
							borderRadius="md"
							cursor={disabled ? "disabled" : "pointer"}
							borderColor={
								value === "all"
									? disabled
										? "emphasis"
										: "primary_emphasis"
									: "default"
							}
							bgColor={
								value === "all" ? (disabled ? "inset" : "primary") : "default"
							}
							onClick={disabled ? undefined : () => onChange("all")}
							style={{
								minWidth: "200px",
								maxWidth: "300px",
							}}
						>
							<RadioInputField
								name="applicable_meal_plans"
								value="all"
								label="All Meal plans"
								help="Default plans added later apply automatically"
								disabled={disabled}
								hideErrors
							/>
						</Box>
						<Box
							flex="1"
							paddingX="4"
							paddingY="2"
							borderWidth="1"
							borderRadius="md"
							cursor={disabled ? "disabled" : "pointer"}
							borderColor={
								value === "specific"
									? disabled
										? "emphasis"
										: "primary_emphasis"
									: "default"
							}
							bgColor={
								value === "specific"
									? disabled
										? "inset"
										: "primary"
									: "default"
							}
							onClick={disabled ? undefined : () => onChange("specific")}
							style={{
								minWidth: "200px",
								maxWidth: "300px",
							}}
						>
							<RadioInputField
								name="applicable_meal_plans"
								value="specific"
								label="Specific Meal plans"
								help="Sold only with the meal plans checked below"
								disabled={disabled}
							/>
						</Box>
					</Inline>
				)}
			</GetFieldValue>
			<GetFieldValue<"all" | "specific"> name="applicable_meal_plans">
				{({ value }) =>
					value === "specific" ? (
						<OtaMealPlansField
							hotelId={hotelId}
							name="ota_meal_plans"
							disabled={disabled}
						/>
					) : null
				}
			</GetFieldValue>
		</Stack>
	)
}

/**
 * Add/Edit room type in a dialog. Edits when `roomType` is passed, otherwise adds.
 * Like the form, it only hands the values over to `onSubmit`.
 */
export function OtaRoomTypeInDialog({
	roomType,
	initialImageUrl,
	hotelId,
	withMealPlanPicker,
	notice,
	unlockCopy,
	open,
	onClose,
	onSubmit,
}: {
	// only passed when editing
	roomType?: IOtaRoomTypeFormSchema
	initialImageUrl?: string
	hotelId?: number
	withMealPlanPicker?: boolean
	notice?: ReactNode
	// only passed when editing, see OtaRoomTypeForm
	unlockCopy?: TUnlockHotelConfigCopy
	open: boolean
	onClose: () => void
	onSubmit: (values: IOtaRoomTypeFormSchema) => Promise<void> | void
}) {
	return (
		<Dialog
			open={open}
			onClose={onClose}
			title={roomType ? "Edit Room" : "Add Room Type"}
			md
		>
			<DialogBody paddingX="0" paddingY="0">
				<OtaRoomTypeForm
					submitLabel={roomType ? "Update" : "Add Room"}
					initialValues={roomType}
					initialImageUrl={initialImageUrl}
					hotelId={hotelId}
					withMealPlanPicker={withMealPlanPicker}
					notice={notice}
					unlockCopy={unlockCopy}
					onSubmit={onSubmit}
				/>
			</DialogBody>
		</Dialog>
	)
}

function GuestCountInputField({
	label,
	name,
	min,
	disabled,
}: {
	label: string
	name: string
	min: number
	disabled: boolean
}) {
	return (
		<TextInputField
			name={name}
			label={label}
			type="number"
			placeholder="e.g. 2"
			min={min}
			disabled={disabled}
			style={{ maxWidth: "8rem", cursor: disabled ? "not-allowed" : undefined }}
			className={noSpinnersClassName}
		/>
	)
}
